/**
 * Assessor firm families — ADR-0067 (v10.3 Track 5), ADR-0081 (v10.6 Track 6).
 * Pure: no tables.
 *
 * Each family names its roles, the determination kind it writes and its
 * independence rule (mode + window). Migrations 335b and 533 seed the same
 * values into `assessor_firm_types` (the DB triggers read them); this module is
 * the code side and the two must agree (a test asserts it).
 *
 * The program manifest's `assessorTypes` is the gate (V103-5-3): a family
 * can be invited to, and write on, a period only when the period's program
 * lists it.
 *
 * ADR-0081 adds `cpa` (SOC 2) and `certification_body` (ISO/IEC 42001):
 * schema in migrations 533–535 (V106-6-2), routes in V106-6-3. Migration 533
 * is the documented prerequisite of any code that names the two families; the
 * firm tier also checks the family's `assessor_firm_types` row before it
 * declares or invites one (409 `assessor_family_not_enabled` instead of a
 * trigger 500 when code reaches a database 533 hasn't reached yet).
 */
import { getProgramManifest } from '../programs/manifests.js';
/** Every assessor family (`assessor_firm_types` rows, migrations 335b + 533). */
export const ASSESSOR_FIRM_TYPES = ['c3pao', 'govramp_3pao', 'fedramp_recognized', 'cpa', 'certification_body'];
const SPECS = {
    c3pao: {
        type: 'c3pao', label: 'CMMC C3PAO', lead: 'lead_cca', member: 'cca', decisionRole: null,
        roleLabels: { lead: 'Lead CCA', member: 'CCA' }, determinationKind: 'c3pao',
        // 32 CFR 170.8(b)(17)(ii)(G): 3 years; the CMMC rule set may tighten it (independence_lookback_months).
        independence: { rule: '32 CFR 170.8(b)(17)(ii)(G)', mode: 'refuse', window: 'lookback', defaultLookbackMonths: 36 },
        externalIdLabel: 'Cyber AB C3PAO id', independenceBasis: 'regulation_default', flagMessage: null,
        independenceScope: 'workspace', acceptanceAttestation: null, reportKinds: [], singleOpenEngagementPerPeriod: false,
    },
    govramp_3pao: {
        type: 'govramp_3pao', label: 'GovRAMP 3PAO', lead: 'lead_assessor', member: 'assessor', decisionRole: null,
        roleLabels: { lead: 'Lead assessor', member: 'Assessor' }, determinationKind: 'assessor',
        // GovRAMP relies on FedRAMP 3PAO independence; flag only until GovRAMP's rule is confirmed (ADR-0067 owner decision 1).
        independence: { rule: 'FedRAMP REC-IAS-SEP (applied to GovRAMP)', mode: 'flag', window: 'lookback', defaultLookbackMonths: 24 },
        externalIdLabel: 'GovRAMP 3PAO listing id', independenceBasis: 'govramp_rule_unconfirmed',
        flagMessage: 'advisory within 2 years — GovRAMP rule unconfirmed',
        independenceScope: 'workspace', acceptanceAttestation: null, reportKinds: ['rar', 'sap'], singleOpenEngagementPerPeriod: false,
    },
    fedramp_recognized: {
        type: 'fedramp_recognized', label: 'FedRAMP Recognized Assessor', lead: 'lead_assessor', member: 'assessor', decisionRole: null,
        roleLabels: { lead: 'Lead assessor', member: 'Assessor' }, determinationKind: 'assessor',
        // FedRAMP Consolidated Rules 2026, REC-IAS-SEP: no independent assessment within 2 years of advising the offering.
        independence: { rule: 'FedRAMP REC-IAS-SEP', mode: 'refuse', window: 'lookback', defaultLookbackMonths: 24 },
        externalIdLabel: 'FedRAMP recognition id', independenceBasis: 'default_pending_rule', flagMessage: null,
        independenceScope: 'workspace', acceptanceAttestation: null, reportKinds: ['rar', 'sap'], singleOpenEngagementPerPeriod: false,
    },
    cpa: {
        type: 'cpa', label: 'CPA firm (SOC 2)', lead: 'lead_assessor', member: 'assessor', decisionRole: null,
        roleLabels: { lead: 'Engagement partner', member: 'Engagement staff' }, determinationKind: 'assessor',
        // AICPA Code (as updated through September 2026; rechecked 2026-10-14): no fixed lookback; the window is the
        // period covered by the attest report (ET 0.400.41) + the period of the professional engagement (ET 0.400.42),
        // per ET 1.295.010 .01 and 1.297.030 .03. Impairment depends on the 1.295.030/.040 safeguards: flag only.
        // Work that is part of the SOC 2 examination itself is not a separate service (ET 1.295.010 .04); preparing
        // the system description is a management responsibility (ET 1.295.030 j).
        independence: { rule: 'AICPA ET 1.295.010 .01, 1.297.030 .03 (period covered by the attest report + period of the professional engagement, ET 0.400.41–.42)', mode: 'flag', window: 'subject_period', defaultLookbackMonths: null },
        externalIdLabel: 'CPA firm permit / peer review id', independenceBasis: 'aicpa_et_1.295_subject_period',
        flagMessage: 'related services in the SOC 2 period — firm to document its independence evaluation (AICPA ET 1.295)',
        independenceScope: 'workspace', acceptanceAttestation: 'recorded', reportKinds: [], singleOpenEngagementPerPeriod: false,
    },
    certification_body: {
        type: 'certification_body', label: 'Certification body (ISO/IEC 42001)', lead: 'lead_assessor', member: 'assessor', decisionRole: 'technical_reviewer',
        roleLabels: { lead: 'Lead auditor', member: 'Auditor', decision: 'Certification decision maker' }, determinationKind: 'assessor',
        // ISO/IEC 17021-1:2015 §5.2.6 / 5.2.7 / 5.2.10: two years after internal audits or consultancy end. Refuse.
        independence: { rule: 'ISO/IEC 17021-1:2015 5.2.6, 5.2.7, 5.2.10', mode: 'refuse', window: 'lookback', defaultLookbackMonths: 24 },
        externalIdLabel: 'Accreditation (body + number) for ISO/IEC 42001', independenceBasis: 'iso17021-1_5.2_24m', flagMessage: null,
        independenceScope: 'organization', acceptanceAttestation: 'required', reportKinds: [], singleOpenEngagementPerPeriod: true,
    },
};
export const ASSESSOR_FIRM_TYPE_SPECS = Object.freeze(SPECS);
/**
 * V112-2-1 / ADR-0081 §7 (T6-L5, owner PD-4 option c): the families whose assessor surface is still in preview.
 * Inviting or accepting an engagement of one of them needs the preview enabled for the CLIENT workspace (the
 * Worker's `c3paoPreviewEnabled`), so no engagement exists where the firm's screens would 404. Declaring the
 * family stays as it is: operator authorization already gates it. Remove a family here at its launch.
 */
export const PREVIEW_ASSESSOR_FAMILIES = Object.freeze(['cpa', 'certification_body']);
export async function previewFamilyAllowed(gate, workspaceId, family) {
    if (!PREVIEW_ASSESSOR_FAMILIES.includes(family))
        return true;
    return gate ? Boolean(await gate(workspaceId, family)) : false;
}
/** The statement the accepting lead attests (V106-6-5 T6-M1). */
export const INDEPENDENCE_ATTESTATION_STATEMENT = 'No management system consultancy, internal audits or related services for this client, by the firm or by personnel assigned, within the independence window.';
export function isAssessorFirmType(v) {
    return typeof v === 'string' && ASSESSOR_FIRM_TYPES.includes(v);
}
/** The assessor families a program accepts (its manifest `assessorTypes` ∩ the families). */
export function assessorTypesForProgram(programKey) {
    if (!programKey)
        return [];
    const m = getProgramManifest(programKey);
    return (m?.assessorTypes ?? []).filter(isAssessorFirmType);
}
/** V103-5-3: the manifest gate. */
export function programAllowsAssessorType(programKey, type) {
    return assessorTypesForProgram(programKey).includes(type);
}
//# sourceMappingURL=assessor-firm-types.js.map